// ═══════════════════════════════════════════════════════
//  CONSULTEC FIELD MANAGER — Main Entry Point
//  src/main.js
// ═══════════════════════════════════════════════════════

import { initFirebase }            from './services/firebase.js'
import { loginDemo, loginConEmail, logout, getUser, getRole, getPlan, onAuthStateChanged, resetPassword, ROLES, PERMISOS } from './services/auth.js'
import { proyectos, bitacora, incidencias, fases, personal, documentos, estimaciones, reportes, setActiveProject, getActiveProject, obtenerGPS } from './services/db.js'
import { DEMO_DATA }               from './utils/demoData.js'
import { uploadFotoObra }          from './services/cloudinary.js'
import { generarReporteSemanal, generarReporteIncidencia, generarInformeEjecutivo } from './services/pdfGenerator.js'

// ── ESTADO GLOBAL ────────────────────────────────────────
const STATE = {
  screen:       'splash',   // splash | roleSelect | login | projSelect | app
  selectedRole: null,
  activeProject: null,
  data: {
    proyectos: [], bitacora: [], incidencias: [],
    fases: [], personal: [], estimaciones: [], documentos: []
  },
  limits: { bitacora: { used: 0, max: 5 }, incidencias: { used: 0, max: 2 }, fotos: { used: 0, max: 3 }, tareas: { used: 0, max: 3 } },
  planActivo: 'demo',
  subs: []  // Firestore listeners
}

// ── INIT ─────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', async () => {
  console.log('🚀 CONSULTEC Field Manager iniciando...')

  // Intentar inicializar Firebase
  initFirebase()

  // Montar app en #app
  document.getElementById('app').innerHTML = buildApp()

  // Iniciar en splash
  showScreen('splash')

  // Escuchar estado de red
  window.addEventListener('online',  () => toggleOfflineBanner(false))
  window.addEventListener('offline', () => toggleOfflineBanner(true))

  // Vincular eventos globales
  bindEvents()

  console.log('✅ App montada')
})

// ── BUILD HTML ────────────────────────────────────────────
function buildApp () {
  return `
  <!-- OFFLINE BANNER -->
  <div class="offline-bar" id="offlineBanner">⚡ Sin conexión — trabajando en modo offline. Los cambios se sincronizarán al reconectarse.</div>

  <!-- PANTALLAS PREVIAS AL DASHBOARD -->
  <div id="screenWrapper">

    <!-- S1: SPLASH -->
    <div id="splash" class="screen-full" style="background:var(--navy);min-height:100vh;display:none;flex-direction:column;align-items:center;justify-content:center;position:relative;overflow:hidden">
      <div style="position:absolute;inset:0;background-image:radial-gradient(circle,rgba(255,255,255,0.055) 1px,transparent 1px);background-size:36px 36px;pointer-events:none"></div>
      <div style="position:absolute;width:500px;height:500px;border-radius:50%;background:rgba(240,122,35,0.07);top:-150px;right:-150px;filter:blur(70px);pointer-events:none"></div>
      <div style="position:absolute;width:400px;height:400px;border-radius:50%;background:rgba(26,95,180,0.08);bottom:-100px;left:-100px;filter:blur(60px);pointer-events:none"></div>
      <div style="position:relative;z-index:5;display:flex;flex-direction:column;align-items:center;text-align:center;padding:32px">
        ${logoSVG(90)}
        <div style="font-family:'Syne',sans-serif;font-size:28px;font-weight:800;color:#fff;letter-spacing:3px;margin-top:20px">CONSULTEC</div>
        <div style="font-size:9px;color:rgba(255,255,255,0.35);letter-spacing:3.5px;margin-top:4px;font-family:'Syne',sans-serif">FIELD MANAGER</div>
        <div style="width:40px;height:2px;background:var(--orange);border-radius:2px;margin:18px auto"></div>
        <p style="font-size:13px;color:rgba(255,255,255,0.45);max-width:260px;line-height:1.7;font-weight:300;margin-bottom:28px">Supervisión, seguimiento y cierre de obra — desde el campo</p>
        <div style="display:flex;gap:8px;flex-wrap:wrap;justify-content:center;margin-bottom:28px">
          <span style="font-size:9px;padding:4px 12px;border-radius:20px;background:rgba(29,158,117,0.15);color:#5DCAA5;border:1px solid rgba(29,158,117,0.2)">📍 GPS en tiempo real</span>
          <span style="font-size:9px;padding:4px 12px;border-radius:20px;background:rgba(240,122,35,0.15);color:#F07A23;border:1px solid rgba(240,122,35,0.2)">📄 Reportes automáticos</span>
          <span style="font-size:9px;padding:4px 12px;border-radius:20px;background:rgba(26,95,180,0.15);color:#85B7EB;border:1px solid rgba(26,95,180,0.2)">✅ Cierre digital</span>
        </div>
        <button class="btn btn-orange" style="padding:14px 48px;font-size:14px;letter-spacing:0.5px" onclick="APP.goRoleSelect()">Iniciar sesión →</button>
        <button style="margin-top:12px;background:transparent;color:rgba(255,255,255,0.45);border:1px solid rgba(255,255,255,0.15);padding:11px 40px;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:12px;cursor:pointer" onclick="APP.goDemo()">Ver demo sin registro</button>
        <div style="position:absolute;bottom:16px;font-size:10px;color:rgba(255,255,255,0.2);letter-spacing:1px">v1.0 · CONSULTEC SA de CV · 2026</div>
      </div>
    </div>

    <!-- S2: ROLE SELECT -->
    <div id="roleSelect" class="screen-full" style="background:var(--navy);min-height:100vh;display:none;flex-direction:column;align-items:center;justify-content:center;padding:32px 16px;position:relative;overflow:hidden">
      <div style="position:absolute;inset:0;background-image:radial-gradient(circle,rgba(255,255,255,0.04) 1px,transparent 1px);background-size:36px 36px;pointer-events:none"></div>
      <div style="background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.09);border-radius:20px;padding:28px;width:100%;max-width:480px;position:relative;z-index:2">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:24px;justify-content:center">
          ${logoSVG(26)} <div><div style="font-family:'Syne',sans-serif;font-size:14px;font-weight:800;color:#fff;letter-spacing:2px">CONSULTEC</div><div style="font-size:7px;color:rgba(255,255,255,0.3);letter-spacing:2px">FIELD MANAGER</div></div>
        </div>
        <div style="font-family:'Syne',sans-serif;font-size:18px;font-weight:800;color:#fff;margin-bottom:4px">¿Cuál es tu rol?</div>
        <div style="font-size:12px;color:rgba(255,255,255,0.4);margin-bottom:20px;font-weight:300">Cada rol tiene permisos específicos de acceso</div>
        <div id="roleGrid" style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:20px"></div>
        <button id="btnContinue" class="btn btn-orange" style="width:100%;padding:12px;font-size:13px;opacity:0.4;pointer-events:none" onclick="APP.goLogin()">Selecciona un rol para continuar</button>
        <button style="display:block;width:100%;text-align:center;margin-top:10px;background:transparent;border:none;color:rgba(255,255,255,0.35);cursor:pointer;font-size:11px" onclick="APP.goSplash()">← Regresar</button>
      </div>
    </div>

    <!-- S3: LOGIN -->
    <div id="loginScreen" class="screen-full" style="background:var(--navy);min-height:100vh;display:none;flex-direction:column;align-items:center;justify-content:center;padding:32px 16px;position:relative;overflow:hidden">
      <div style="position:absolute;inset:0;background-image:radial-gradient(circle,rgba(255,255,255,0.04) 1px,transparent 1px);background-size:36px 36px;pointer-events:none"></div>
      <div style="background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.09);border-radius:20px;padding:28px;width:100%;max-width:380px;position:relative;z-index:2" id="loginCard">
      </div>
    </div>

    <!-- S4: PROJECT SELECT -->
    <div id="projSelect" class="screen-full" style="min-height:100vh;display:none;flex-direction:column;background:var(--bg)">
    </div>

  </div><!-- /screenWrapper -->

  <!-- APP SHELL (Dashboard) -->
  <div id="appShell" style="display:none;flex;height:100vh;overflow:hidden">
  </div>

  <!-- MODALS CONTAINER -->
  <div id="modalsContainer"></div>

  <!-- TOAST CONTAINER -->
  <div class="toast-container" id="toastContainer"></div>
  `
}

// ── LOGO SVG ─────────────────────────────────────────────
function logoSVG (size = 36) {
  return `<svg width="${size}" height="${size}" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0">
    <rect width="38" height="38" rx="8" fill="#F07A23"/>
    <rect x="52" width="38" height="38" rx="8" fill="#1A5FB4"/>
    <rect x="52" y="52" width="38" height="38" rx="8" fill="rgba(255,255,255,0.12)"/>
    <rect y="52" width="38" height="38" rx="8" fill="rgba(255,255,255,0.06)"/>
    <rect x="22" y="22" width="46" height="46" rx="8" fill="rgba(255,255,255,0.97)"/>
    <path d="M36 45h18M45 36v18" stroke="#07183A" stroke-width="3.5" stroke-linecap="round"/>
  </svg>`
}

// ── NAVEGACIÓN DE PANTALLAS ──────────────────────────────
function showScreen (name) {
  STATE.screen = name
  const all = ['splash','roleSelect','loginScreen','projSelect','appShell']
  all.forEach(id => {
    const el = document.getElementById(id)
    if (el) el.style.display = 'none'
  })
  const target = document.getElementById(name === 'login' ? 'loginScreen' : name)
  if (target) target.style.display = 'flex'
}

// ── RENDER ROLES ──────────────────────────────────────────
function renderRoleGrid () {
  const grid = document.getElementById('roleGrid')
  if (!grid) return

  const roles = [
    { key: ROLES.DIRECTOR,    label: 'Director / Gerente', icon: '🏢', desc: 'Ve y exporta todo', perm: 'Lectura total + Exportar', permClass: 'badge-free' },
    { key: ROLES.SUPERVISOR,  label: 'Supervisor de obra', icon: '👷', desc: 'Registra su área',  perm: 'Solo sus registros',    permClass: 'badge-pro'  },
    { key: ROLES.RESIDENTE,   label: 'Residente de obra',  icon: '📋', desc: 'Valida en sitio',   perm: 'Registra su sección',   permClass: 'badge-pro'  },
    { key: ROLES.ADMIN,       label: 'Administrativo',     icon: '📊', desc: 'Finanzas y docs',   perm: 'Financiero + Docs',     permClass: 'badge-warn' },
    { key: ROLES.CLIENTE,     label: 'Cliente / Propietario', icon: '👔', desc: 'Solo lectura', perm: 'Solo lectura',           permClass: 'badge-pend' },
    { key: ROLES.CONTRATISTA, label: 'Contratista',        icon: '🔧', desc: 'Sus partidas',      perm: 'Sus actividades',       permClass: 'badge-pro'  }
  ]

  grid.innerHTML = roles.map(r => `
    <div class="role-card" data-role="${r.key}" onclick="APP.selectRole('${r.key}', '${r.label}')"
      style="background:rgba(255,255,255,0.05);border:1.5px solid rgba(255,255,255,0.09);border-radius:12px;padding:14px;cursor:pointer;transition:all 0.2s;position:relative">
      <div style="font-size:20px;margin-bottom:6px">${r.icon}</div>
      <div style="font-family:'Syne',sans-serif;font-size:11px;font-weight:700;color:#fff">${r.label}</div>
      <div style="font-size:10px;color:rgba(255,255,255,0.4);margin-top:2px">${r.desc}</div>
      <span class="badge ${r.permClass}" style="margin-top:6px;display:inline-block">${r.perm}</span>
    </div>
  `).join('')
}

// ── RENDER LOGIN CARD ────────────────────────────────────
function renderLoginCard () {
  const card = document.getElementById('loginCard')
  if (!card) return
  const rolInfo = STATE.selectedRole ? PERMISOS[STATE.selectedRole] : null

  card.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:20px;justify-content:center">
      ${logoSVG(22)}
      <div style="font-family:'Syne',sans-serif;font-size:13px;font-weight:800;color:#fff;letter-spacing:2px">CONSULTEC</div>
    </div>
    ${rolInfo ? `
    <div style="background:rgba(240,122,35,0.12);border:1px solid rgba(240,122,35,0.25);border-radius:8px;padding:10px 12px;margin-bottom:18px;display:flex;align-items:center;gap:8px">
      <span style="font-size:18px">${rolInfo.icon}</span>
      <div>
        <div style="font-family:'Syne',sans-serif;font-size:11px;font-weight:700;color:#fff">${rolInfo.label}</div>
        <div style="font-size:9px;color:rgba(255,255,255,0.4)">${rolInfo.descripcion}</div>
      </div>
      <span style="margin-left:auto;font-size:10px;color:var(--orange);cursor:pointer" onclick="APP.goRoleSelect()">Cambiar →</span>
    </div>` : ''}
    <div style="font-family:'Syne',sans-serif;font-size:17px;font-weight:800;color:#fff;margin-bottom:4px">Bienvenido</div>
    <div style="font-size:11px;color:rgba(255,255,255,0.4);margin-bottom:18px;font-weight:300">Ingresa tus credenciales de acceso</div>
    <div class="form-group">
      <label class="form-label" style="color:rgba(255,255,255,0.4)">CORREO O USUARIO</label>
      <input id="loginEmail" type="text" style="background:rgba(255,255,255,0.07);border:1.5px solid rgba(255,255,255,0.1);border-radius:9px;padding:11px 14px;font-size:13px;color:#fff;outline:none;width:100%;font-family:'DM Sans',sans-serif" placeholder="supervisor@consultec.mx" value="ing.hernandez@consultec.mx"/>
    </div>
    <div class="form-group">
      <label class="form-label" style="color:rgba(255,255,255,0.4)">CONTRASEÑA</label>
      <input id="loginPass" type="password" style="background:rgba(255,255,255,0.07);border:1.5px solid rgba(255,255,255,0.1);border-radius:9px;padding:11px 14px;font-size:13px;color:#fff;outline:none;width:100%;font-family:'DM Sans',sans-serif" placeholder="••••••••"/> style="background:rgba(255,255,255,0.07);border:1.5px solid rgba(255,255,255,0.1);border-radius:9px;padding:11px 14px;font-size:13px;color:#fff;outline:none;width:100%;font-family:'DM Sans',sans-serif" placeholder="••••••••" value="••••••••"/><span onclick="const i=document.getElementById('loginPass');i.type=i.type==='password'?'text':'password';this.textContent=i.type==='password'?'👁':' 🙈'" style="position:absolute;right:12px;top:50%;transform:translateY(-50%);cursor:pointer;color:#94A3B8;font-size:14px">👁</span>
    </div>
    <div class="form-group">
      <label class="form-label" style="color:rgba(255,255,255,0.4)">EMPRESA</label>
      <input id="loginOrg" type="text" style="background:rgba(255,255,255,0.07);border:1.5px solid rgba(255,255,255,0.1);border-radius:9px;padding:11px 14px;font-size:13px;color:#fff;outline:none;width:100%;font-family:'DM Sans',sans-serif" placeholder="CONSULTEC SA de CV" value="CONSULTEC SA de CV"/>
    </div>
    <div id="loginError" style="color:var(--red);font-size:11px;margin-bottom:8px;display:none"></div>
    <button class="btn btn-orange" style="width:100%;padding:12px;font-size:13px;letter-spacing:0.3px" onclick="APP.doLogin()">Ingresar al sistema →</button>
    <div style="display:flex;align-items:center;gap:10px;margin:14px 0"><div style="flex:1;height:1px;background:rgba(255,255,255,0.08)"></div><span style="font-size:10px;color:rgba(255,255,255,0.25)">o</span><div style="flex:1;height:1px;background:rgba(255,255,255,0.08)"></div></div>
    <button style="width:100%;background:transparent;color:rgba(255,255,255,0.5);border:1px solid rgba(255,255,255,0.12);padding:10px;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:12px;cursor:pointer" onclick="APP.forgotPassword()">¿Olvidaste tu contraseña? <span style="color:#F07B20;text-decoration:underline">Recuperar acceso</span></button>
    <button style="width:100%;background:transparent;color:rgba(255,255,255,0.5);border:1px solid rgba(255,255,255,0.12);padding:10px;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:12px;cursor:pointer;margin-top:8px" onclick="APP.goDemo()">Entrar con cuenta demo</button>
    <button style="display:block;width:100%;text-align:center;margin-top:10px;background:transparent;border:none;color:rgba(255,255,255,0.3);cursor:pointer;font-size:11px" onclick="APP.goRoleSelect()">← Cambiar rol</button>
  `
}

// ── RENDER PROJECT SELECT ────────────────────────────────
async function renderProjSelect (user, role) {
  const el = document.getElementById('projSelect')
  if (!el) return

  const isDirector = [ROLES.DIRECTOR, ROLES.ADMIN].includes(role)
  const rolInfo = PERMISOS[role] || {}
  const proysList = await proyectos.getAll()
  const misProy = isDirector ? proysList : proysList.filter(p => p.supervisorUid === user?.uid || p.supervisorUid?.includes('demo'))
  const otrosProy = isDirector ? [] : proysList.filter(p => !misProy.find(m => m.id === p.id))

  el.innerHTML = `
    <div style="background:var(--navy2);padding:12px 16px;display:flex;align-items:center;justify-content:space-between">
      <div style="display:flex;align-items:center;gap:8px">${logoSVG(22)}<div style="font-family:'Syne',sans-serif;font-size:12px;font-weight:800;color:#fff;letter-spacing:1.5px">CONSULTEC</div></div>
      <div style="display:flex;align-items:center;gap:8px">
        <div style="width:24px;height:24px;border-radius:50%;background:var(--orange);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;color:#fff">${getInitials(user?.nombre || 'US')}</div>
        <div><div style="font-size:10px;color:rgba(255,255,255,0.7);font-weight:500">${user?.nombre || 'Usuario'}</div><div style="font-size:8px;color:rgba(255,255,255,0.35)">${rolInfo.label || role}</div></div>
      </div>
    </div>
    <div style="flex:1;overflow-y:auto;padding:16px 14px 32px">
      <div style="background:var(--navy);border-radius:12px;padding:16px;margin-bottom:14px;position:relative;overflow:hidden">
        <div style="position:absolute;inset:0;background-image:radial-gradient(circle,rgba(255,255,255,0.04) 1px,transparent 1px);background-size:28px 28px;pointer-events:none"></div>
        <div style="position:relative;z-index:2">
          <div style="font-size:11px;color:rgba(255,255,255,0.4)">Bienvenido de regreso,</div>
          <div style="font-family:'Syne',sans-serif;font-size:18px;font-weight:800;color:#fff">${user?.nombre || 'Usuario'}</div>
          <div style="font-size:11px;color:rgba(255,255,255,0.4);margin-top:2px">Hoy ${new Date().toLocaleDateString('es-MX', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</div>
          <div style="display:inline-flex;align-items:center;gap:5px;background:rgba(240,122,35,0.15);border:1px solid rgba(240,122,35,0.25);border-radius:20px;padding:4px 12px;margin-top:8px">
            <div style="width:5px;height:5px;border-radius:50%;background:var(--orange)"></div>
            <div style="font-size:9px;color:var(--orange);font-family:'Syne',sans-serif;font-weight:600">${rolInfo.label?.toUpperCase()}</div>
          </div>
          <div style="font-size:10px;color:rgba(255,255,255,0.3);margin-top:8px;line-height:1.5">${rolInfo.descripcion || ''}</div>
        </div>
      </div>
      <div style="font-family:'Syne',sans-serif;font-size:10px;font-weight:700;color:var(--navy);letter-spacing:1px;margin-bottom:10px">${isDirector ? 'TODOS LOS PROYECTOS — VISTA EJECUTIVA' : 'TUS PROYECTOS ASIGNADOS'}</div>
      <div style="display:flex;flex-direction:column;gap:10px">
        ${misProy.map(p => proyCard(p, true)).join('')}
        ${otrosProy.map(p => proyCard(p, false)).join('')}
        <div onclick="APP.showNewProjectModal()" style="background:var(--bg);border:1.5px dashed var(--border);border-radius:12px;padding:14px;display:flex;align-items:center;gap:10px;cursor:pointer">
          <span style="font-size:22px;color:var(--muted)">＋</span>
          <div><div style="font-size:12px;font-weight:600;color:var(--muted)">Agregar nuevo proyecto</div><div style="font-size:10px;color:rgba(90,106,138,0.6)">Vivienda, hotel, hospital, plaza...</div></div>
        </div>
      </div>
      <div style="margin-top:14px">
        <button class="btn btn-ghost" style="font-size:10px" onclick="APP.doLogout()">← Cerrar sesión</button>
      </div>
    </div>
  `
}

function proyCard (p, mine) {
  const fis = p.avanceFisico || 0
  const fin = p.avanceFinanciero || 0
  const tipos = { desarrollo_vertical:'🏗', hotel_resort:'🏝', hospital_escuela:'🏥', oficinas_plaza:'🏢', vivienda:'🏠' }
  const icon = tipos[p.tipo] || '🏗'
  const locked = !mine
  return `
  <div onclick="${locked ? `APP.toast('No tienes acceso a este proyecto', 'warn')` : `APP.openProject('${p.id}')`}"
    style="background:var(--card);border:${mine ? '2px solid var(--blue)' : '1px solid var(--border)'};border-radius:12px;padding:14px;cursor:pointer;${locked ? 'opacity:0.5' : ''}transition:all 0.2s">
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
      <div style="width:36px;height:36px;border-radius:9px;background:var(--light);display:flex;align-items:center;justify-content:center;font-size:18px">${icon}</div>
      <div style="flex:1;min-width:0">
        <div style="font-family:'Syne',sans-serif;font-size:13px;font-weight:700;color:var(--navy)">${p.nombre}</div>
        <div style="font-size:10px;color:var(--muted)">${p.empresa || ''} · ${p.ubicacion || ''}</div>
      </div>
      ${mine ? '<span class="badge badge-prog" style="white-space:nowrap">Tu proyecto</span>' : '<span style="font-size:12px">🔒</span>'}
    </div>
    <div style="display:flex;flex-direction:column;gap:5px">
      <div style="display:flex;align-items:center;gap:8px"><span style="font-size:9px;color:var(--muted);width:64px">Físico</span><div class="progress-track" style="flex:1"><div class="progress-fill" style="width:${fis}%;background:var(--blue)"></div></div><span style="font-size:9px;font-weight:600;color:var(--navy);min-width:28px;text-align:right">${fis}%</span></div>
      <div style="display:flex;align-items:center;gap:8px"><span style="font-size:9px;color:var(--muted);width:64px">Financiero</span><div class="progress-track" style="flex:1"><div class="progress-fill" style="width:${fin}%;background:var(--amber)"></div></div><span style="font-size:9px;font-weight:600;color:var(--navy);min-width:28px;text-align:right">${fin}%</span></div>
    </div>
    <div style="display:flex;gap:6px;margin-top:8px;align-items:center">
      <span class="badge badge-done">En ejecución</span>
      <span style="font-size:9px;color:var(--muted)">Sem ${p.semanaActual}/${p.semanasTotales} · ${p.incidenciasAbiertas || 0} incidencias</span>
    </div>
  </div>`
}

// ── API PÚBLICA ──────────────────────────────────────────
window.APP = {
  goSplash: () => { showScreen('splash') },

  goRoleSelect: () => {
    showScreen('roleSelect')
    renderRoleGrid()
  },

  selectRole: (role, label) => {
    STATE.selectedRole = role
    document.querySelectorAll('.role-card').forEach(c => {
      const isThis = c.dataset.role === role
      c.style.borderColor = isThis ? 'var(--orange)' : 'rgba(255,255,255,0.09)'
      c.style.background  = isThis ? 'rgba(240,122,35,0.12)' : 'rgba(255,255,255,0.05)'
      if (isThis) c.innerHTML += '<span style="position:absolute;top:8px;right:8px;color:var(--orange);font-size:12px;font-weight:700">✓</span>'
    })
    const btn = document.getElementById('btnContinue')
    if (btn) { btn.textContent = `Continuar como ${label} →`; btn.style.opacity = '1'; btn.style.pointerEvents = 'auto' }
  },

  goLogin: () => {
    if (!STATE.selectedRole) { APP.toast('Selecciona un rol primero','warn'); return }
    showScreen('login')
    renderLoginCard()
  },

  doLogin: async () => {
    const email = document.getElementById('loginEmail')?.value || ''
    const pass  = document.getElementById('loginPass')?.value  || ''
    const result = await loginConEmail(email, pass, STATE.selectedRole, 'CONSULTEC SA de CV')
    if (!result.ok) {
      const errEl = document.getElementById('loginError')
      if (errEl) { errEl.textContent = result.error; errEl.style.display = 'block' }
      return
    }
    await APP._afterLogin(result.user, result.role)
  },

  showAddPersonaModal: () => {
    const overlay = document.createElement('div')
    overlay.id = 'addPersonaOverlay'
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:1000;display:flex;align-items:center;justify-content:center'
    overlay.innerHTML = `
      <div style="background:#1a2744;border-radius:16px;padding:28px;width:90%;max-width:400px;box-shadow:0 20px 60px rgba(0,0,0,0.4)">
        <h3 style="font-family:'DM Sans',sans-serif;font-size:16px;font-weight:700;color:#fff;margin-bottom:20px">Agregar persona al equipo</h3>
        <div style="display:flex;flex-direction:column;gap:12px">
          <input id="ap-nombre" placeholder="Nombre completo *" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none" />
          <select id="ap-rol" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none">
            <option value="">Rol / Puesto *</option>
            <option value="Supervisor general">Supervisor general</option>
            <option value="Ingeniero residente">Ingeniero residente</option>
            <option value="Jefe de cuadrilla">Jefe de cuadrilla</option>
            <option value="Subcontratista">Subcontratista</option>
            <option value="Operario">Operario</option>
            <option value="Auxiliar">Auxiliar</option>
          </select>
          <select id="ap-categoria" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none">
            <option value="ing">Ingeniero</option>
            <option value="operario">Operario</option>
            <option value="subcontratista">Subcontratista</option>
          </select>
          <input id="ap-zona" placeholder="Zona / Área asignada" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none" />
          <input id="ap-telefono" placeholder="Teléfono (opcional)" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none" />
        </div>
        <div style="display:flex;gap:10px;margin-top:20px">
          <button onclick="document.getElementById('addPersonaOverlay').remove()" style="flex:1;background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px;color:rgba(255,255,255,0.6);font-family:'DM Sans',sans-serif;font-size:13px;cursor:pointer">Cancelar</button>
          <button onclick="APP._guardarPersona()" style="flex:2;background:#F07B20;border:none;border-radius:8px;padding:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:700;cursor:pointer">Agregar →</button>
        </div>
      </div>`
    document.body.appendChild(overlay)
  },

  _guardarPersona: async () => {
    const nombre = document.getElementById('ap-nombre')?.value?.trim()
    const rol    = document.getElementById('ap-rol')?.value
    if (!nombre || !rol) { APP.toast('Nombre y rol son obligatorios', 'warn'); return }
    const data = {
      nombre,
      rol,
      categoria:  document.getElementById('ap-categoria')?.value || 'operario',
      zona:       document.getElementById('ap-zona')?.value?.trim() || '',
      telefono:   document.getElementById('ap-telefono')?.value?.trim() || '',
      online:     true,
      proyectoId: STATE.activeProject
    }
    try {
      await personal.agregar(data)
      if (!STATE.data.personal) STATE.data.personal = []
      STATE.data.personal.push(data)
      document.getElementById('addPersonaOverlay')?.remove()
      APP.toast('✅ Persona agregada al equipo', 'ok')
      renderSection('personal')
    } catch (e) {
      APP.toast('Error: ' + e.message, 'error')
    }
  },

  subirFotos: async (files) => {
    if (!files || !files.length) return
    const projId = STATE.activeProject || 'demo'
    APP.toast(`Subiendo ${files.length} foto(s)...`, 'info')
    let subidas = 0
    let errores = 0
    for (const file of Array.from(files)) {
      try {
        let lat = null, lng = null
        try {
          const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 }))
          lat = pos.coords.latitude
          lng = pos.coords.longitude
        } catch {}
        const zona = document.getElementById('np-zona')?.value || 'General'
        const result = await uploadFotoObra(file, { projectId: projId, zona, lat, lng, userId: getUser()?.uid || 'demo' })
        if (!STATE.data.fotos) STATE.data.fotos = []
        STATE.data.fotos.unshift(result)
        subidas++
      } catch (e) {
        errores++
        console.error('Error subiendo foto:', e)
      }
    }
    if (subidas > 0) {
      APP.toast(`✅ ${subidas} foto(s) subida(s) correctamente`, 'ok')
      if (STATE.screen === 'dashboard') renderSection('fotos')
    }
    if (errores > 0) APP.toast(`⚠️ ${errores} foto(s) no se pudieron subir`, 'warn')
  },

  showNewProjectModal: () => {
    const overlay = document.createElement('div')
    overlay.id = 'newProjOverlay'
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:1000;display:flex;align-items:center;justify-content:center'
    overlay.innerHTML = `
      <div style="background:#1a2744;border-radius:16px;padding:28px;width:90%;max-width:420px;box-shadow:0 20px 60px rgba(0,0,0,0.4)">
        <h3 style="font-family:'DM Sans',sans-serif;font-size:16px;font-weight:700;color:#fff;margin-bottom:20px">Nuevo Proyecto</h3>
        <div style="display:flex;flex-direction:column;gap:12px">
          <input id="np-nombre" placeholder="Nombre del proyecto *" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none" />
          <input id="np-cliente" placeholder="Cliente" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none" />
          <input id="np-ubicacion" placeholder="Ubicación" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none" />
          <select id="np-tipo" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none">
            <option value="">Tipo de proyecto</option>
            <option value="vivienda">Vivienda</option>
            <option value="hotel">Hotel</option>
            <option value="hospital">Hospital</option>
            <option value="plaza">Plaza comercial</option>
            <option value="oficinas">Oficinas</option>
            <option value="industrial">Industrial</option>
            <option value="otro">Otro</option>
          </select>
          <input id="np-presupuesto" placeholder="Presupuesto (MXN)" type="number" style="background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px 14px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;outline:none" />
        </div>
        <div style="display:flex;gap:10px;margin-top:20px">
          <button onclick="document.getElementById('newProjOverlay').remove()" style="flex:1;background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:10px;color:rgba(255,255,255,0.6);font-family:'DM Sans',sans-serif;font-size:13px;cursor:pointer">Cancelar</button>
          <button onclick="APP._guardarNuevoProyecto()" style="flex:2;background:#F07B20;border:none;border-radius:8px;padding:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:700;cursor:pointer">Crear Proyecto →</button>
        </div>
      </div>`
    document.body.appendChild(overlay)
  },

  _guardarNuevoProyecto: async () => {
    const nombre = document.getElementById('np-nombre')?.value?.trim()
    if (!nombre) { APP.toast('El nombre del proyecto es obligatorio', 'warn'); return }
    const data = {
      nombre,
      cliente:      document.getElementById('np-cliente')?.value?.trim() || '',
      ubicacion:    document.getElementById('np-ubicacion')?.value?.trim() || '',
      tipo:         document.getElementById('np-tipo')?.value || 'otro',
      presupuesto:  parseFloat(document.getElementById('np-presupuesto')?.value) || 0,
      fechaInicio:  new Date().toISOString().split('T')[0],
      status:       'activo'
    }
    try {
      await proyectos.crear(data)
      document.getElementById('newProjOverlay')?.remove()
      APP.toast('✅ Proyecto creado correctamente', 'ok')
      showScreen('projSelect')
      await renderProjSelect(getUser(), getRole())
    } catch (e) {
      APP.toast('Error al crear proyecto: ' + e.message, 'error')
    }
  },

  forgotPassword: async () => {
    const email = document.getElementById('loginEmail')?.value?.trim() || ''
    if (!email) {
      APP.toast('Escribe tu correo primero', 'warn')
      return
    }
    const result = await resetPassword(email)
    if (result.ok) {
      APP.toast('✅ Correo de recuperación enviado a ' + email, 'ok')
    } else {
      APP.toast(result.error, 'error')
    }
  },

  goDemo: async () => {
    const result = loginDemo('demo@consultec.mx', ROLES.SUPERVISOR)
    await APP._afterLogin(result.user, result.role)
  },

  _afterLogin: async (user, role) => {
    STATE.planActivo = user.plan || 'demo'
    showScreen('projSelect')
    await renderProjSelect(user, role)
  },

  openProject: async (projId) => {
    STATE.activeProject = projId
    setActiveProject(projId)
    const proj = await proyectos.get(projId) || DEMO_DATA.proyectos.find(p => p.id === projId)
    STATE.data.proyecto = proj
    await loadProjectData(projId)
    renderAppShell(proj)
    showScreen('appShell')
    APP.toast(`Proyecto cargado: ${proj.nombre}`, 'info')
  },

  doLogout: async () => {
    await logout()
    STATE.screen = 'splash'
    STATE.activeProject = null
    STATE.selectedRole = null
    showScreen('splash')
    APP.toast('Sesión cerrada', '')
  },

  toast: (msg, type = '') => {
    const c = document.getElementById('toastContainer')
    if (!c) return
    const t = document.createElement('div')
    t.className = `toast ${type}`
    t.innerHTML = msg
    c.appendChild(t)
    setTimeout(() => t.remove(), 3400)
  },

  generarReportePDF: async (tipo) => {
    const user = getUser()
    if (STATE.planActivo === 'demo') {
      APP.toast('Generar reportes requiere Plan Pro 🔒', 'warn')
      return
    }
    APP.toast(`⏳ Generando reporte ${tipo}...`, 'info')
    const proj = STATE.data.proyecto
    try {
      let file
      if (tipo === 'semanal') {
        file = await generarReporteSemanal({ proyecto: proj, fases: STATE.data.fases, incidencias: STATE.data.incidencias, bitacora: STATE.data.bitacora, semana: proj?.semanaActual })
      } else if (tipo === 'ejecutivo') {
        file = await generarInformeEjecutivo(STATE.data.proyectos.length ? STATE.data.proyectos : DEMO_DATA.proyectos)
      }
      if (file) APP.toast(`✅ PDF descargado: ${file}`, '')
    } catch (err) { APP.toast('Error generando PDF: ' + err.message, 'error') }
  }
}

// ── CARGAR DATOS DEL PROYECTO ────────────────────────────
async function loadProjectData (projId) {
  const isDemo = STATE.planActivo === 'demo'
  try {
    const [fases_, bita_, inc_, pers_, est_, docs_] = await Promise.all([
      fases.getAll(projId), bitacora.getAll(projId), incidencias.getAll(projId),
      personal.getAll(), estimaciones.getAll(), documentos.getAll()
    ])
    STATE.data.fases        = fases_.length        ? fases_  : (isDemo ? DEMO_DATA.fases        : [])
    STATE.data.bitacora     = bita_.length          ? bita_   : (isDemo ? DEMO_DATA.bitacora     : [])
    STATE.data.incidencias  = inc_.length           ? inc_    : (isDemo ? DEMO_DATA.incidencias  : [])
    STATE.data.personal     = pers_.length          ? pers_   : (isDemo ? DEMO_DATA.personal     : [])
    STATE.data.estimaciones = est_.length           ? est_    : (isDemo ? DEMO_DATA.estimaciones : [])
    STATE.data.documentos   = docs_.length          ? docs_   : (isDemo ? DEMO_DATA.documentos   : [])
  } catch (err) {
    console.warn('Error cargando datos:', err)
    STATE.data.fases        = isDemo ? DEMO_DATA.fases        : []
    STATE.data.bitacora     = isDemo ? DEMO_DATA.bitacora     : []
    STATE.data.incidencias  = isDemo ? DEMO_DATA.incidencias  : []
    STATE.data.personal     = isDemo ? DEMO_DATA.personal     : []
    STATE.data.estimaciones = isDemo ? DEMO_DATA.estimaciones : []
    STATE.data.documentos   = isDemo ? DEMO_DATA.documentos   : []
  }
}

// ── RENDER APP SHELL ─────────────────────────────────────
function renderAppShell (proj) {
  const shell = document.getElementById('appShell')
  if (!shell) return
  const user = getUser()
  const rol  = getRole()

  shell.style.display = 'flex'
  shell.innerHTML = `
    <style>
    .app-sidebar{width:210px;background:var(--navy);display:flex;flex-direction:column;flex-shrink:0;height:100vh}
    .app-main{flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0}
    .app-content{flex:1;overflow-y:auto;padding:14px 16px 32px}
    .kpi-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-bottom:14px}
    .kpi-card{background:var(--card);border:1px solid var(--border);border-radius:11px;padding:13px 15px;position:relative}
    .kpi-val{font-family:'Syne',sans-serif;font-size:24px;font-weight:800;color:var(--navy);line-height:1}
    .kpi-lbl{font-size:9px;color:var(--muted);letter-spacing:0.8px;margin-bottom:5px}
    .two-col{display:grid;grid-template-columns:1.5fr 1fr;gap:12px;margin-bottom:14px}
    .three-col{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:14px}
    .gantt-tbl{width:100%;border-collapse:collapse}
    .gantt-tbl th{font-size:8px;color:var(--muted);font-weight:600;padding:6px 10px;border-bottom:1px solid var(--border);text-align:left;background:var(--bg)}
    .gantt-tbl td{padding:8px 10px;border-bottom:1px solid rgba(221,228,240,0.4);vertical-align:middle;font-size:11px}
    .gantt-tbl tr:hover td{background:var(--light)}
    .fab-bar{display:flex;gap:8px;flex-wrap:wrap;margin-top:12px}
    @media(max-width:768px){.kpi-grid{grid-template-columns:1fr 1fr}.two-col,.three-col{grid-template-columns:1fr}}
    </style>

    <!-- SIDEBAR -->
    <div class="app-sidebar">
      <div style="padding:12px;border-bottom:1px solid rgba(255,255,255,0.07);display:flex;align-items:center;gap:8px">
        ${logoSVG(24)}
        <div><div style="font-family:'Syne',sans-serif;font-size:11px;font-weight:800;color:#fff;letter-spacing:1.5px">CONSULTEC</div><div style="font-size:7px;color:rgba(255,255,255,0.3);letter-spacing:2px">FIELD MANAGER</div></div>
      </div>
      <div style="padding:6px 8px;margin:8px 6px 2px;background:rgba(255,255,255,0.05);border-radius:8px;border:1px solid rgba(255,255,255,0.07);cursor:pointer" onclick="APP.toast('Cambiar proyecto desde el menú de proyectos','info')">
        <div style="font-size:7px;color:rgba(255,255,255,0.3);letter-spacing:1.5px">PROYECTO ACTIVO</div>
        <div style="font-size:10px;color:#fff;font-weight:500;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${proj?.nombre || 'Proyecto'}</div>
        <div style="display:flex;align-items:center;gap:4px;margin-top:3px"><div style="width:5px;height:5px;border-radius:50%;background:var(--green)"></div><div style="font-size:8px;color:rgba(255,255,255,0.35)">En ejecución · Sem ${proj?.semanaActual || '—'}</div></div>
      </div>
      <div style="flex:1;overflow-y:auto;padding:4px 0">
        <div style="font-size:7px;color:rgba(255,255,255,0.25);letter-spacing:1.5px;padding:8px 14px 2px">SUPERVISIÓN</div>
        <div class="nav-item active" id="nav-dashboard"     onclick="APP_NAV.go('dashboard',this)">📊 Dashboard</div>
        <div class="nav-item" id="nav-bitacora"             onclick="APP_NAV.go('bitacora',this)">📋 Bitácora digital</div>
        <div class="nav-item" id="nav-incidencias"          onclick="APP_NAV.go('incidencias',this)">⚠ Incidencias <span class="badge badge-red" style="margin-left:auto">${proj?.incidenciasAbiertas || 0}</span></div>
        <div class="nav-item" id="nav-fotos"                onclick="APP_NAV.go('fotos',this)">📷 Avance fotográfico</div>
        <div style="font-size:7px;color:rgba(255,255,255,0.25);letter-spacing:1.5px;padding:8px 14px 2px">SEGUIMIENTO</div>
        <div class="nav-item" id="nav-tareas"               onclick="APP_NAV.go('tareas',this)">✅ Tareas</div>
        <div class="nav-item" id="nav-cronograma"           onclick="APP_NAV.go('cronograma',this)">📅 Cronograma</div>
        <div class="nav-item" id="nav-personal"             onclick="APP_NAV.go('personal',this)">👷 Personal en sitio</div>
        <div class="nav-item" id="nav-financiero"           onclick="APP_NAV.go('financiero',this)">📈 Financiero <span class="badge badge-pro" style="margin-left:auto;font-size:7px">PRO</span></div>
        <div style="font-size:7px;color:rgba(255,255,255,0.25);letter-spacing:1.5px;padding:8px 14px 2px">CIERRE</div>
        <div class="nav-item" id="nav-documentos"           onclick="APP_NAV.go('documentos',this)">📁 Documentos</div>
        <div class="nav-item" id="nav-cierre"               onclick="APP_NAV.go('cierre',this)">🏁 Cierre de obra <span class="badge badge-ent" style="margin-left:auto;font-size:7px">ENT</span></div>
        <div class="nav-item" id="nav-reportes"             onclick="APP_NAV.go('reportes',this)">📄 Reportes PDF</div>
      </div>
      <div style="padding:10px;border-top:1px solid rgba(255,255,255,0.07)">
        <div style="display:flex;align-items:center;gap:7px">
          <div style="width:24px;height:24px;border-radius:50%;background:var(--orange);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;color:#fff">${getInitials(user?.nombre || 'US')}</div>
          <div><div style="font-size:10px;color:rgba(255,255,255,0.7);font-weight:500">${user?.nombre || 'Usuario'}</div><div style="font-size:8px;color:rgba(255,255,255,0.3)">${PERMISOS[rol]?.label || rol}</div></div>
          <button style="margin-left:auto;background:transparent;border:none;color:rgba(255,255,255,0.25);cursor:pointer;font-size:13px" onclick="APP.doLogout()" title="Cerrar sesión">⏏</button>
        </div>
      </div>
    </div>

    <!-- MAIN -->
    <div class="app-main">
      <div class="topbar">
        <div style="display:flex;align-items:center;gap:10px">
          <div class="topbar-title" id="appPageTitle">Dashboard</div>
          <span class="badge badge-pro" id="appPlanBadge">${STATE.planActivo === 'demo' ? '🔓 Demo' : STATE.planActivo === 'enterprise' ? '✅ Enterprise' : '✅ Pro'}</span>
        </div>
        <div style="display:flex;align-items:center;gap:8px">
          <span style="font-size:10px;color:var(--muted)">${new Date().toLocaleDateString('es-MX', {day:'numeric',month:'short',year:'numeric'})}</span>
          <div class="badge badge-red" style="cursor:pointer;font-size:9px;padding:4px 10px" onclick="APP_NAV.go('incidencias',null)">⚠ ${proj?.incidenciasAbiertas || 0} alertas</div>
          <button class="btn btn-orange" id="appMainBtn" onclick="APP_NAV.primaryAction()">+ Registro</button>
        </div>
      </div>
      <div class="app-content" id="appContent"></div>
    </div>
  `

  // Iniciar con el dashboard
  APP_NAV.go('dashboard', document.getElementById('nav-dashboard'))
}

// ── NAVEGACIÓN INTERNA DE LA APP ─────────────────────────
window.APP_NAV = {
  current: 'dashboard',

  go (section, navEl) {
    this.current = section
    document.querySelectorAll('.nav-item').forEach(n => n.classList.remove('active'))
    if (navEl) navEl.classList.add('active')
    const titles = {
      dashboard:'Dashboard', bitacora:'Bitácora digital', incidencias:'Incidencias',
      fotos:'Avance fotográfico', tareas:'Tareas', cronograma:'Cronograma',
      personal:'Personal en sitio', financiero:'Módulo financiero',
      documentos:'Planos y documentos', cierre:'Cierre de obra', reportes:'Reportes PDF'
    }
    const titleEl = document.getElementById('appPageTitle')
    if (titleEl) titleEl.textContent = titles[section] || section
    this.renderSection(section)
  },

  primaryAction () {
    const actions = {
      dashboard:    () => APP.toast('Usa los botones rápidos del dashboard','info'),
      bitacora:     () => APP.toast('Abre el compositor de notas abajo','info'),
      incidencias:  () => APP.toast('Usa el botón + Incidencia del módulo','info'),
      reportes:     () => APP.generarReportePDF('semanal'),
      default:      () => APP.toast('Acción rápida del módulo activo','info')
    }
    ;(actions[this.current] || actions.default)()
  },

  renderSection (section) {
    const el = document.getElementById('appContent')
    if (!el) return
    const proj = STATE.data.proyecto || {}
    const renderers = {
      dashboard:   () => renderDashboard(proj),
      bitacora:    () => renderBitacora(),
      incidencias: () => renderIncidencias(),
      fotos:       () => renderFotos(),
      cronograma:  () => renderCronograma(),
      personal:    () => renderPersonal(),
      reportes:    () => renderReportes(proj),
      default:     () => `<div style="text-align:center;padding:48px;color:var(--muted)"><div style="font-size:32px;margin-bottom:10px">🔒</div><div style="font-family:'Syne',sans-serif;font-size:14px;font-weight:700;color:var(--navy);margin-bottom:6px">${section.charAt(0).toUpperCase()+section.slice(1)}</div><div>Este módulo está disponible en plan Pro o Enterprise.</div><button class="btn btn-orange" style="margin-top:14px" onclick="APP.toast('Activar plan — disponible en producción con Stripe','info')">Activar plan</button></div>`
    }
    el.innerHTML = (renderers[section] || renderers.default)()
  }
}

// ── RENDERS DE SECCIONES ─────────────────────────────────
function renderDashboard (proj) {
  const inc = STATE.data.incidencias
  const bit = STATE.data.bitacora
  const fas = STATE.data.fases
  return `
  <div class="kpi-grid">
    <div class="kpi-card" style="border-top:3px solid var(--blue)">
      <div class="kpi-lbl">AVANCE FÍSICO</div><div class="kpi-val">${proj.avanceFisico || 0}<span style="font-size:13px;font-weight:400">%</span></div>
      <div class="progress-track" style="margin-top:8px"><div class="progress-fill" style="width:${proj.avanceFisico||0}%;background:var(--blue)"></div></div>
      <div style="font-size:10px;color:var(--green);margin-top:4px">▲ +4% esta semana</div>
    </div>
    <div class="kpi-card" style="border-top:3px solid var(--orange)">
      <div class="kpi-lbl">AVANCE FINANCIERO</div><div class="kpi-val">${proj.avanceFinanciero || 0}<span style="font-size:13px;font-weight:400">%</span></div>
      <div class="progress-track" style="margin-top:8px"><div class="progress-fill" style="width:${proj.avanceFinanciero||0}%;background:var(--orange)"></div></div>
      <div style="font-size:10px;color:var(--amber);margin-top:4px">⚠ ${Math.abs((proj.avanceFisico||0)-(proj.avanceFinanciero||0))}% debajo del físico</div>
    </div>
    <div class="kpi-card" style="border-top:3px solid var(--amber)">
      <div class="kpi-lbl">DÍAS TRANSCURRIDOS</div><div class="kpi-val">${proj.diasTranscurridos || 142}</div>
      <div class="progress-track" style="margin-top:8px"><div class="progress-fill" style="width:${Math.round(((proj.diasTranscurridos||142)/(proj.diasContractuales||200))*100)}%;background:var(--amber)"></div></div>
      <div style="font-size:10px;color:var(--muted);margin-top:4px">de ${proj.diasContractuales || 200} días contractuales</div>
    </div>
    <div class="kpi-card" style="border-top:3px solid var(--red)">
      <div class="kpi-lbl">INCIDENCIAS ABIERTAS</div><div class="kpi-val" style="color:var(--red)">${inc.length || 0}</div>
      <div style="display:flex;gap:5px;margin-top:7px">
        <span class="badge badge-red">${inc.filter(i=>i.severidad==='alta').length} Alta</span>
        <span class="badge badge-warn">${inc.filter(i=>i.severidad==='media').length} Media</span>
      </div>
      <div style="font-size:10px;color:var(--red);margin-top:4px">Requieren atención hoy</div>
    </div>
  </div>

  <div class="two-col">
    <div class="card">
      <div class="card-head"><div class="card-title">Cronograma de fases — Sem ${proj.semanaActual}/${proj.semanasTotales}</div><button class="btn btn-ghost" style="font-size:10px;padding:4px 9px" onclick="APP_NAV.go('cronograma',document.getElementById('nav-cronograma'))">Ver completo</button></div>
      <table class="gantt-tbl">${fas.map(f => `
        <tr><td><div style="font-size:12px;font-weight:500;color:var(--navy)">${f.nombre}</div></td><td style="color:var(--muted)">${f.responsable||'—'}</td>
        <td><div class="progress-track" style="width:100px"><div class="progress-fill" style="width:${f.pct}%;background:${f.status==='completado'?'var(--green)':f.status==='demora'?'var(--amber)':'var(--blue)'}"></div></div></td>
        <td style="text-align:right;font-weight:600;font-size:10px;color:${f.status==='completado'?'var(--green)':f.status==='demora'?'var(--amber)':'var(--blue)'}">${f.pct}%</td>
        <td><span class="badge ${f.status==='completado'?'badge-done':f.status==='demora'?'badge-warn':f.status==='progreso'?'badge-prog':'badge-pend'}">${f.status==='completado'?'✓ Listo':f.status==='demora'?'⚠ Demora':f.status==='progreso'?'En progreso':'Pendiente'}</span></td></tr>
      `).join('')}</table>
    </div>
    <div class="card">
      <div class="card-head"><div class="card-title">⚠ Alertas activas</div><span class="badge badge-red">${inc.length} abiertas</span></div>
      ${inc.slice(0,4).map(i => `
      <div style="padding:10px 14px;border-bottom:1px solid rgba(221,228,240,0.4);display:flex;gap:9px;cursor:pointer">
        <div style="width:4px;border-radius:2px;background:${i.severidad==='alta'?'var(--red)':'var(--amber)'};align-self:stretch;flex-shrink:0;min-height:32px"></div>
        <div><div style="font-size:11px;font-weight:500;color:var(--navy)">${i.titulo}</div><div style="font-size:9px;color:var(--muted);margin-top:2px">${i.autorNombre} · ${i.zona||'—'}</div><span class="badge ${i.severidad==='alta'?'badge-red':'badge-warn'}" style="margin-top:3px">${i.severidad?.toUpperCase()||'—'}</span></div>
      </div>`).join('')}
    </div>
  </div>

  <div class="three-col">
    <div class="card">
      <div class="card-head"><div class="card-title">📋 Últimas notas</div></div>
      ${bit.slice(0,2).map(b => `
      <div style="padding:10px 14px;border-bottom:1px solid rgba(221,228,240,0.4)">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
          <div style="width:18px;height:18px;border-radius:50%;background:var(--blue);display:flex;align-items:center;justify-content:center;font-size:7px;font-weight:700;color:#fff">${getInitials(b.autorNombre||'?')}</div>
          <span style="font-size:10px;font-weight:500;color:var(--navy)">${b.autorNombre}</span>
          <span class="badge badge-prog" style="font-size:7px">${b.zona||'—'}</span>
        </div>
        <div style="font-size:10px;color:var(--muted);line-height:1.4">${(b.texto||'').substring(0,80)}...</div>
      </div>`).join('')}
    </div>
    <div class="card">
      <div class="card-head"><div class="card-title">📊 Financiero</div><span class="badge badge-pro">PRO</span></div>
      <div style="padding:12px 14px">
        <div style="margin-bottom:8px"><div style="display:flex;justify-content:space-between;font-size:10px;color:var(--muted);margin-bottom:3px"><span>Físico</span><strong>${proj.avanceFisico||0}%</strong></div><div class="progress-track"><div class="progress-fill" style="width:${proj.avanceFisico||0}%;background:var(--blue)"></div></div></div>
        <div style="margin-bottom:8px"><div style="display:flex;justify-content:space-between;font-size:10px;color:var(--muted);margin-bottom:3px"><span>Financiero</span><strong>${proj.avanceFinanciero||0}%</strong></div><div class="progress-track"><div class="progress-fill" style="width:${proj.avanceFinanciero||0}%;background:var(--orange)"></div></div></div>
        <div style="background:var(--bg);border:1px dashed var(--border);border-radius:8px;padding:8px;text-align:center;margin-top:8px"><div style="font-size:9px;color:var(--muted)">Estimaciones y pagos</div><div style="font-size:10px;color:var(--orange);font-weight:600;cursor:pointer" onclick="APP.toast('Plan Pro requirido para módulo financiero completo','warn')">🔒 Plan Pro →</div></div>
      </div>
    </div>
    <div class="card">
      <div class="card-head"><div class="card-title">👷 Personal en sitio</div></div>
      ${STATE.data.personal.slice(0,4).map(p => `
      <div style="padding:7px 14px;border-bottom:1px solid rgba(221,228,240,0.3);display:flex;align-items:center;gap:7px">
        <div style="width:5px;height:5px;border-radius:50%;background:${p.online?'var(--green)':'var(--muted)'};flex-shrink:0"></div>
        <div style="width:22px;height:22px;border-radius:50%;background:var(--navy);display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:700;color:#fff">${getInitials(p.nombre)}</div>
        <div><div style="font-size:11px;font-weight:500;color:var(--navy)">${p.nombre}</div><div style="font-size:9px;color:var(--muted)">${p.rol}</div></div>
        <div style="margin-left:auto;font-size:9px;color:var(--blue)">${p.zona||'—'}</div>
      </div>`).join('')}
    </div>
  </div>

  <div class="fab-bar">
    <button class="btn btn-orange" onclick="APP.toast('Abre bitácora para nueva nota','info')">📋 Nueva nota</button>
    <button class="btn btn-orange" onclick="APP.toast('Abre incidencias para registrar','info')">⚠ Nueva incidencia</button>
    <button class="btn" style="background:var(--navy);color:#fff" onclick="APP.toast('Foto + GPS: activa en app móvil','info')">📷 Foto + GPS</button>
    <button class="btn btn-ghost" onclick="APP.generarReportePDF('semanal')">📄 Reporte semanal PDF</button>
    <button class="btn btn-ghost" onclick="APP.generarReportePDF('ejecutivo')">🎯 Informe ejecutivo</button>
  </div>`
}

function renderBitacora () {
  const bit = STATE.data.bitacora
  const user = getUser()
  const isDemo = STATE.planActivo === 'demo'
  const usadas = STATE.limits.bitacora.used + bit.filter(b => b.autorUid === user?.uid || b.autorUid?.includes('demo-sup')).length
  const max = STATE.limits.bitacora.max
  return `
  ${isDemo ? `<div class="demo-banner pro"><span style="font-size:18px">⏱</span><div style="flex:1;font-size:11px;color:var(--text);line-height:1.5"><strong style="color:var(--orange)">Demo activa</strong> — Máximo <strong>${max} notas</strong>. Un supervisor registra 10–30 notas diarias.</div><button class="btn btn-orange" style="font-size:10px;padding:5px 12px;flex-shrink:0" onclick="APP.toast('Activar plan con Stripe en producción','info')">Activar Pro →</button></div>
  <div class="limit-bar"><span style="font-size:10px;color:var(--muted)">Notas usadas</span><div class="progress-track" style="flex:1"><div class="progress-fill" style="width:${Math.min(100,(usadas/max)*100)}%;background:var(--orange)"></div></div><span style="font-size:11px;font-weight:600;color:var(--navy)">${usadas} / ${max}</span></div>` : ''}
  <div style="display:flex;gap:8px;padding:10px 0 4px;flex-wrap:wrap">
    <button class="btn btn-ghost" style="font-size:10px;background:var(--navy);color:#fff;padding:5px 12px">Todas</button>
    <button class="btn btn-ghost" style="font-size:10px;padding:5px 12px">Solo las mías</button>
    <button class="btn btn-ghost" style="font-size:10px;padding:5px 12px">Avance</button>
    <button class="btn btn-ghost" style="font-size:10px;padding:5px 12px">Observaciones</button>
  </div>
  ${bit.map(b => {
    const mine = b.autorUid === user?.uid || b.autorUid?.includes('demo-sup')
    return `<div style="background:var(--card);border:1px solid var(--border);border-left:3px solid ${mine?'var(--orange)':'var(--border)'};border-radius:11px;padding:13px;margin-bottom:8px">
      <div style="display:flex;align-items:center;gap:7px;margin-bottom:7px">
        <div style="width:20px;height:20px;border-radius:50%;background:${mine?'var(--navy)':'var(--blue)'};display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:700;color:#fff">${getInitials(b.autorNombre||'?')}</div>
        <span style="font-size:11px;font-weight:500;color:var(--navy)">${b.autorNombre}</span>
        <span class="badge badge-prog" style="font-size:8px">${b.zona||'—'}</span>
        ${mine ? '<span class="badge badge-pro" style="font-size:8px">Tuya ✓</span>' : ''}
        <span style="font-size:9px;color:var(--muted);margin-left:auto">${new Date(b.createdAt).toLocaleTimeString('es-MX',{hour:'2-digit',minute:'2-digit'})}</span>
      </div>
      <div style="font-size:12px;color:var(--text);line-height:1.5;margin-bottom:6px">${b.texto}</div>
      ${b.fotos?.length ? `<div style="background:var(--bg);border:1px solid var(--border);border-radius:7px;padding:6px 10px;font-size:10px;color:var(--blue);margin-bottom:6px">📷 ${b.fotos.length} foto${b.fotos.length>1?'s':''} · GPS adjunto</div>` : ''}
      ${mine ? `<div style="display:flex;gap:6px;margin-top:6px"><button class="btn btn-ghost" style="font-size:9px;padding:3px 9px" onclick="APP.toast('Editar nota — disponible','info')">✏ Editar</button><button class="btn btn-ghost" style="font-size:9px;padding:3px 9px;color:var(--red)" onclick="APP.toast('Nota eliminada','warn')">🗑 Eliminar</button></div>` : `<div style="font-size:9px;color:var(--muted);margin-top:6px;border-top:1px solid var(--border);padding-top:6px">🔒 Solo lectura — registrado por ${b.autorNombre}</div>`}
    </div>`}).join('')}
  <div style="background:var(--card);border:1.5px solid var(--border);border-radius:11px;padding:14px;margin-top:8px">
    <div style="font-family:'Syne',sans-serif;font-size:11px;font-weight:700;color:var(--navy);margin-bottom:10px">✏ Nueva nota de campo</div>
    <div style="display:flex;gap:8px;margin-bottom:8px">
      <input id="bcZona" style="flex:1;background:var(--bg);border:1.5px solid var(--border);border-radius:8px;padding:8px 12px;font-size:12px;font-family:'DM Sans',sans-serif;color:var(--text);outline:none" placeholder="Zona / área"/>
      <select id="bcCat" style="background:var(--bg);border:1.5px solid var(--border);border-radius:8px;padding:8px;font-size:12px;color:var(--text);outline:none"><option>Avance</option><option>Calidad</option><option>Observación</option><option>Seguridad</option></select>
    </div>
    <textarea id="bcDesc" rows="3" style="width:100%;background:var(--bg);border:1.5px solid var(--border);border-radius:8px;padding:9px 12px;font-size:12px;font-family:'DM Sans',sans-serif;color:var(--text);outline:none;resize:vertical;margin-bottom:8px" placeholder="Describe la actividad o situación registrada..."></textarea>
    <div style="display:flex;gap:8px;align-items:center">
      <button class="btn btn-ghost" style="font-size:10px" onclick="APP.toast('Cámara activa en app móvil 📷','info')">📷 Foto</button>
      <button class="btn btn-ghost" style="font-size:10px" onclick="APP.toast('GPS registrado automáticamente 📍','')">📍 GPS</button>
      <button class="btn btn-orange" style="margin-left:auto" onclick="APP.toast('Nota guardada en Firestore ✅','')">Guardar nota →</button>
    </div>
  </div>`
}

function renderIncidencias () {
  const inc = STATE.data.incidencias
  return `
  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:14px">
    <div style="background:var(--card);border:1px solid var(--border);border-radius:10px;padding:12px"><div style="font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:var(--red)">${inc.length}</div><div style="font-size:9px;color:var(--muted)">ABIERTAS</div></div>
    <div style="background:var(--card);border:1px solid var(--border);border-radius:10px;padding:12px"><div style="font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:var(--red)">${inc.filter(i=>i.severidad==='alta').length}</div><div style="font-size:9px;color:var(--muted)">ALTA PRIORIDAD</div></div>
    <div style="background:var(--card);border:1px solid var(--border);border-radius:10px;padding:12px"><div style="font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:var(--amber)">${inc.filter(i=>i.status==='revision').length}</div><div style="font-size:9px;color:var(--muted)">EN REVISIÓN</div></div>
    <div style="background:var(--card);border:1px solid var(--border);border-radius:10px;padding:12px"><div style="font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:var(--green)">3</div><div style="font-size:9px;color:var(--muted)">CERRADAS HOY</div></div>
  </div>
  ${inc.map(i => `
  <div style="background:var(--card);border:1px solid ${i.severidad==='alta'?'#F7C1C1':'var(--border)'};border-radius:11px;padding:13px;margin-bottom:8px;display:flex;gap:10px">
    <div style="width:5px;border-radius:3px;background:${i.severidad==='alta'?'var(--red)':'var(--amber)'};align-self:stretch;flex-shrink:0;min-height:40px"></div>
    <div style="flex:1">
      <div style="font-size:13px;font-weight:600;color:var(--navy)">${i.titulo}</div>
      <div style="font-size:11px;color:var(--muted);margin-top:3px;line-height:1.5">${i.descripcion}</div>
      <div style="display:flex;gap:7px;margin-top:7px;flex-wrap:wrap;align-items:center">
        <span class="badge ${i.status==='abierta'?'badge-red':'badge-warn'}">${i.status}</span>
        <span style="font-size:10px;color:var(--blue)">${i.responsable||'Sin asignar'}</span>
        <span style="font-size:10px;color:var(--muted)">${i.zona||'—'}</span>
      </div>
    </div>
    <div style="display:flex;flex-direction:column;align-items:flex-end;gap:5px">
      <span class="badge ${i.severidad==='alta'?'badge-red':'badge-warn'}">${i.severidad?.toUpperCase()}</span>
      <span style="font-size:9px;color:var(--muted)">📷 ${i.fotos?.length||0} fotos</span>
    </div>
  </div>`).join('')}
  <button class="btn btn-orange" style="width:100%;padding:11px;margin-top:8px" onclick="APP.toast('Registro de incidencia — formulario en módulo','info')">⚠ + Nueva incidencia</button>`
}

function renderFotos () {
  const projId = STATE.activeProject || 'demo'
  const fotos = STATE.data.fotos || []
  const fotosHTML = fotos.length ? fotos.map(f => `
    <div style="position:relative;border-radius:10px;overflow:hidden;aspect-ratio:1;background:#0a1628">
      <img src="${f.url}" style="width:100%;height:100%;object-fit:cover" />
      <div style="position:absolute;bottom:0;left:0;right:0;padding:8px;background:linear-gradient(transparent,rgba(0,0,0,0.7));font-size:10px;color:rgba(255,255,255,0.8)">${f.zona || ''} ${f.lat ? '📍' : ''}</div>
    </div>`).join('') : '<div style="grid-column:1/-1;text-align:center;padding:40px;color:var(--muted);font-size:13px">No hay fotos aún. Sube la primera.</div>'
  return `
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px">
      <div style="font-size:13px;color:var(--muted)">${fotos.length} foto${fotos.length !== 1 ? 's' : ''} registrada${fotos.length !== 1 ? 's' : ''}</div>
      <label style="background:var(--orange);color:#fff;padding:9px 18px;border-radius:9px;font-size:13px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;gap:6px">
        📷 Subir foto
        <input type="file" accept="image/*" multiple style="display:none" onchange="APP.subirFotos(this.files)" />
      </label>
    </div>
    <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:12px">
      ${fotosHTML}
    </div>`
}

function renderCronograma () {
  const fas = STATE.data.fases
  return `
  <div style="background:var(--card);border:1px solid var(--border);border-radius:11px;overflow:hidden;margin-bottom:12px">
    <div class="card-head"><div class="card-title">Cronograma completo — Fases y subfases</div><button class="btn btn-ghost" style="font-size:10px;padding:4px 9px" onclick="APP.toast('Agregar fase — formulario disponible','info')">+ Nueva fase</button></div>
    <div style="overflow-x:auto">
    <table class="gantt-tbl" style="min-width:560px">
      <thead><tr><th>Fase / Actividad</th><th>Responsable</th><th>Inicio</th><th>Fin</th><th>Estado</th><th style="min-width:120px">Progreso</th><th style="text-align:right">%</th></tr></thead>
      <tbody>${fas.map(f => `
        <tr><td><div style="font-size:12px;font-weight:500;color:var(--navy)">${f.nombre}</div></td>
        <td style="color:var(--muted)">${f.responsable||'—'}</td>
        <td style="font-size:10px;color:var(--muted)">${f.fechaInicio||'—'}</td>
        <td style="font-size:10px;color:var(--muted)">${f.fechaFin||'—'}</td>
        <td><span class="badge ${f.status==='completado'?'badge-done':f.status==='demora'?'badge-warn':f.status==='progreso'?'badge-prog':'badge-pend'}">${f.status==='completado'?'✓ Completado':f.status==='demora'?'⚠ Demora':f.status==='progreso'?'En progreso':'Pendiente'}</span></td>
        <td><div class="progress-track" style="width:120px"><div class="progress-fill" style="width:${f.pct}%;background:${f.status==='completado'?'var(--green)':f.status==='demora'?'var(--amber)':'var(--blue)'}"></div></div></td>
        <td style="text-align:right;font-weight:600;font-size:10px;color:${f.status==='completado'?'var(--green)':f.status==='demora'?'var(--amber)':'var(--blue)'}">${f.pct}%</td>
        </tr>`).join('')}</tbody>
    </table></div>
    <div style="padding:9px 16px;font-size:11px;color:var(--muted);cursor:pointer;border-top:1px dashed var(--border)" onclick="APP.toast('+ Agregar nueva fase — disponible','info')">＋ Agregar nueva fase, subfase o actividad</div>
  </div>`
}

function renderPersonal () {
  const pers = STATE.data.personal
  return `
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:14px">
    <div style="background:var(--card);border:1px solid var(--border);border-radius:10px;padding:12px"><div style="font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:var(--navy)">${pers.length}</div><div style="font-size:9px;color:var(--muted)">TOTAL EN SITIO</div></div>
    <div style="background:var(--card);border:1px solid var(--border);border-radius:10px;padding:12px"><div style="font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:var(--blue)">${pers.filter(p=>p.categoria==='ing').length}</div><div style="font-size:9px;color:var(--muted)">INGENIEROS</div></div>
    <div style="background:var(--card);border:1px solid var(--border);border-radius:10px;padding:12px"><div style="font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:var(--green)">${pers.filter(p=>p.categoria==='operario'||p.categoria==='op').length}</div><div style="font-size:9px;color:var(--muted)">OPERARIOS</div></div>
  </div>
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px">
    ${pers.map(p => `
    <div style="background:var(--card);border:1px solid var(--border);border-radius:11px;padding:13px">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:9px">
        <div style="width:32px;height:32px;border-radius:50%;background:var(--navy);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff">${getInitials(p.nombre)}</div>
        <div><div style="font-size:12px;font-weight:600;color:var(--navy)">${p.nombre}</div><div style="font-size:9px;color:var(--muted)">${p.rol}</div></div>
        <div style="width:7px;height:7px;border-radius:50%;background:${p.online?'var(--green)':'var(--muted)'};margin-left:auto"></div>
      </div>
      <div style="font-size:10px;color:var(--blue);font-weight:500">${p.zona||'—'}</div>
    </div>`).join('')}
    <div style="background:var(--bg);border:1.5px dashed var(--border);border-radius:11px;display:flex;align-items:center;justify-content:center;min-height:100px;cursor:pointer" onclick="APP.showAddPersonaModal()">
      <div style="text-align:center"><div style="font-size:22px;color:var(--muted)">＋</div><div style="font-size:10px;color:var(--muted);margin-top:4px">Agregar persona</div></div>
    </div>
  </div>`
}

function renderReportes (proj) {
  const isDemo = STATE.planActivo === 'demo'
  const tipos = [
    { key:'semanal', icon:'📊', label:'Reporte semanal', desc:'Avance + incidencias + fotos', plan:'pro' },
    { key:'incidencia', icon:'⚠', label:'Reporte de incidencia', desc:'Por número de incidencia', plan:'pro' },
    { key:'financiero', icon:'💰', label:'Corte financiero', desc:'Por estimación o periodo', plan:'pro' },
    { key:'cronograma', icon:'📅', label:'Reporte de cronograma', desc:'Gantt + eficiencia + retrasos', plan:'pro' },
    { key:'ejecutivo', icon:'🎯', label:'Informe ejecutivo', desc:'Vista director — KPIs globales', plan:'pro' },
    { key:'cierre', icon:'🏁', label:'Informe de cierre', desc:'Dossier final completo', plan:'enterprise' }
  ]
  return `
  ${isDemo ? `<div class="demo-banner pro"><span style="font-size:18px">📄</span><div style="flex:1;font-size:11px;color:var(--text);line-height:1.5"><strong style="color:var(--orange)">Plan Pro requerido</strong> — Los reportes automáticos generan PDFs en 10 segundos. Manual tarda 4 horas.</div><button class="btn btn-orange" style="font-size:10px;padding:5px 12px;flex-shrink:0" onclick="APP.toast('Activar plan — Stripe en producción','info')">Ver plan →</button></div>` : ''}
  <div style="font-family:'Syne',sans-serif;font-size:10px;font-weight:700;color:var(--navy);letter-spacing:0.5px;margin:12px 0 8px">TIPOS DE REPORTE DISPONIBLES</div>
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px">
    ${tipos.map(t => `
    <div onclick="APP.generarReportePDF('${t.key}')" style="background:var(--card);border:1.5px dashed var(--border);border-radius:10px;padding:14px;text-align:center;cursor:pointer;transition:all 0.15s" onmouseover="this.style.borderColor='var(--orange)'" onmouseout="this.style.borderColor='var(--border)'">
      <div style="font-size:22px;margin-bottom:6px">${t.icon}</div>
      <div style="font-family:'Syne',sans-serif;font-size:11px;font-weight:600;color:var(--navy)">${t.label}</div>
      <div style="font-size:9px;color:var(--muted);margin-top:2px">${t.desc}</div>
      <span class="badge ${t.plan==='enterprise'?'badge-ent':'badge-pro'}" style="margin-top:6px">${t.plan==='enterprise'?'Enterprise':'Pro'}</span>
    </div>`).join('')}
  </div>
  <div style="font-family:'Syne',sans-serif;font-size:10px;font-weight:700;color:var(--navy);letter-spacing:0.5px;margin-bottom:8px">ÚLTIMOS REPORTES</div>
  <div style="background:var(--card);border:1px solid var(--border);border-radius:11px;padding:14px;margin-bottom:8px">
    <div style="display:flex;gap:12px;align-items:center;margin-bottom:12px;padding-bottom:12px;border-bottom:1px solid var(--border)">
      <div style="width:38px;height:38px;border-radius:9px;background:#EAF3DE;display:flex;align-items:center;justify-content:center;font-size:18px">📊</div>
      <div style="flex:1"><div style="font-family:'Syne',sans-serif;font-size:13px;font-weight:700;color:var(--navy)">Reporte Semanal — Semana ${proj.semanaActual||20}</div><div style="font-size:10px;color:var(--muted);margin-top:2px">${new Date().toLocaleDateString('es-MX')} · ${proj.nombre||'Proyecto'}</div></div>
      <span class="badge badge-done">Generado</span>
    </div>
    <div style="display:flex;gap:8px;flex-wrap:wrap">
      <button class="btn btn-red" style="font-size:10px" onclick="APP.generarReportePDF('semanal')">📄 Descargar PDF</button>
      <button class="btn btn-ghost" style="font-size:10px" onclick="APP.toast('Envío WhatsApp en producción 📱','info')">📱 WhatsApp</button>
      <button class="btn btn-ghost" style="font-size:10px" onclick="APP.toast('Envío por correo en producción ✉','info')">✉ Correo</button>
    </div>
  </div>`
}

// ── HELPERS ───────────────────────────────────────────────
function getInitials (name) {
  return (name || 'US').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

function toggleOfflineBanner (offline) {
  const el = document.getElementById('offlineBanner')
  if (el) el.classList.toggle('visible', offline)
}

function bindEvents () {
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') document.querySelectorAll('.modal-overlay').forEach(m => m.remove())
  })
}
